import { forwardRef } from 'react';

import { Box, Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  type RenterInfoRenterAddedDto,
  useRentalManagerRenterContactWebApiHandler_getTenants,
} from '@zg-rentals/rental-manager-env';
import { TenantSelectFormBody, type TenantSelectFormBodyRef } from '@zg-rentals/modal-tenant';

import { createZgApiHttpClient } from '../../../../client/zgApiHttpClient';

import { useSelector } from '../../../selectors/useSelector';
import { RoutingSelectors } from '../../../selectors';

const TOAST_MESSAGE = {
  SUCCESS_ADD_TENANT: 'Tenants added to property dashboard',
} as const;

type AddTenantSelectStepProps = {
  onValidationChange?: (isValid: boolean) => void;
  onSubmitSuccess?: () => void;
};

const AddTenantSelectStep = forwardRef<TenantSelectFormBodyRef, AddTenantSelectStepProps>(
  ({ onValidationChange, onSubmitSuccess }, ref) => {
    const queryClient = useQueryClient();
    const { enqueueToast } = useToast();

    const { env, listingAlias } = useSelector((state) => ({
      env: state.env,
      listingAlias: RoutingSelectors.makeListingId(state),
    }));

    const client = createZgApiHttpClient(env.zgApiPublic);

    const invalidateGetTenantsQuery = () => {
      queryClient.invalidateQueries({
        queryKey: useRentalManagerRenterContactWebApiHandler_getTenants.getKey({ listingAlias }),
      });
    };

    const handleSubmitSuccess = (_addedRenters?: Array<RenterInfoRenterAddedDto>) => {
      invalidateGetTenantsQuery();
      enqueueToast(<Toast tone="success">{TOAST_MESSAGE.SUCCESS_ADD_TENANT}</Toast>);
      onSubmitSuccess?.();
    };

    return (
      <Box data-testid="AddTenantSelectStep">
        <TenantSelectFormBody
          ref={ref}
          client={client}
          listingAlias={listingAlias}
          onSubmitSuccess={handleSubmitSuccess}
          onValidationChange={onValidationChange}
        />
      </Box>
    );
  },
);

AddTenantSelectStep.displayName = 'AddTenantSelectStep';

export default AddTenantSelectStep;
